/** @jsxImportSource @emotion/react */
import { css, useTheme } from '@emotion/react';
import { useMemo, useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

import useMatchMedia from '../hooks/matchMedia';
import useVec3 from '../hooks/Vec3';
import useTick from '../hooks/Tick';

const labels = ['2015', '2016', '2017', '2018', '2019', '2020', '2021', '2022'];
const values = [70, 72, 74, 92, 98, 99, 99, 99];

const calcX = (idx, currentStep, width) => {
	const segmentLength = width / (currentStep + 1);
	return segmentLength * idx + segmentLength * 0.5;
};

const calcY = (value, dataRange, height) => {
	return height - (1 / (dataRange.max - dataRange.min)) * (value - dataRange.min) * height;
};

const springConfig = { damping: 10, mass: 0.1 };
const Graph = ({ currentStep, revealed }) => {
	const theme = useTheme();
	const isDesktop = useMatchMedia(`(min-width: ${theme.breakpoint.m})`);
	const svgDimensions = useMemo(() => ({ width: isDesktop ? 800 : 400, height: isDesktop ? 400 : 300 }), [isDesktop]);

	const dataRange = useMemo(() => {
		return {
			min: Math.max(0, Math.min(...values) - 5),
			max: Math.min(100, Math.max(...values) + 5),
		};
	}, []);

	const [coords] = useState([
		useVec3([svgDimensions.width * 0.5, svgDimensions.height * 0.5, 0], springConfig),
		useVec3([svgDimensions.width * 0.5, svgDimensions.height * 0.5, 0], springConfig),
		useVec3([svgDimensions.width * 0.5, svgDimensions.height * 0.5, 0], springConfig),
		useVec3([svgDimensions.width * 0.5, svgDimensions.height * 0.5, 0], springConfig),
		useVec3([svgDimensions.width * 0.5, svgDimensions.height * 0.5, 0], springConfig),
		useVec3([svgDimensions.width * 0.5, svgDimensions.height * 0.5, 0], springConfig),
		useVec3([svgDimensions.width * 0.5, svgDimensions.height * 0.5, 0], springConfig),
		useVec3([svgDimensions.width * 0.5, svgDimensions.height * 0.5, 0], springConfig),
	]);

	useTick(true);

	useEffect(() => {
		values.forEach((value, idx) => {
			const coord = coords[idx];
			const usedIdx = Math.min(idx, currentStep);
			coord.setX(calcX(usedIdx, currentStep, svgDimensions.width));
			coord.setY(calcY(value, dataRange, svgDimensions.height));
			coord.setZ(idx <= currentStep ? 1 : 0);
		});
	}, [currentStep, svgDimensions.width, svgDimensions.height, coords, dataRange]);

	const chartPath = coords
		.map((coord, idx) => {
			return `${idx === 0 ? 'M' : 'L'}${coord.getX()} ${coord.getY()}`;
		})
		.join('');

	return (
		<div
			css={css`
				width: 100%;
				height: 100%;
				display: flex;
				flex-direction: column;
				justify-content: center;
			`}
		>
			<svg
				viewBox={`0 0 ${svgDimensions.width} ${svgDimensions.height}`}
				css={css`
					display: block;
					overflow: visible;
				`}
			>
				<AnimatePresence>
					<motion.g initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
						{[0, 1, 2, 3].map((n) => (
							<g key={n} transform={`translate(0,${svgDimensions.height - (svgDimensions.height / 3) * n})`}>
								<path
									d={`M50 0L${svgDimensions.width} 0`}
									css={css`
										stroke: ${theme.color.primaryContrast};
										stroke-dasharray: 5;
									`}
								/>
								<text x="32" textAnchor="end" fill={theme.color.primaryContrast}>
									<tspan dy="5">{Math.round(dataRange.min + n * ((dataRange.max - dataRange.min) / 3))} %</tspan>
								</text>
							</g>
						))}
						{values.map((value, idx) => {
							const coord = coords[idx];
							return (
								<g key={labels[idx]} transform={`translate(${coord.getX()},0)`} opacity={coord.getZ()}>
									<text x="0" y={svgDimensions.height} textAnchor="middle" fill={theme.color.primaryContrast}>
										<tspan dy="34">{labels[idx]}</tspan>
									</text>
								</g>
							);
						})}
					</motion.g>
				</AnimatePresence>
				<AnimatePresence>
					{!!revealed && (
						<motion.g
							initial={{ opacity: 0, scale: 0 }}
							animate={{ opacity: 1, scale: 1 }}
							exit={{ opacity: 0, scale: 0 }}
						>
							<path d={chartPath} stroke={theme.color.primaryContrast} strokeWidth="2" fill="none" />
							{values.map((value, idx) => {
								const coord = coords[idx];
								return (
									<g key={labels[idx]} transform={`translate(${coord.getX()},0)`} opacity={coord.getZ()}>
										<path
											d={`M0 0 L0 ${svgDimensions.height + 12}`}
											stroke={theme.color.secondary}
											strokeWidth="1"
											fill="none"
										/>
										<g transform={`translate(0,${coord.getY()})`}>
											<text textAnchor="middle" fill={theme.color.primaryContrast} fontSize="140%">
												<motion.tspan
													animate={{ opacity: currentStep === idx ? 1 : 0 }}
													x="0"
													y="-18"
													opacity="0"
													fontWeight="bold"
												>
													{value} %
												</motion.tspan>
												<motion.tspan animate={{ opacity: currentStep !== idx ? 1 : 0 }} y="-18" x="0" opacity="0">
													{value} %
												</motion.tspan>
											</text>
											<circle
												cx="0"
												cy="0"
												r={5}
												fill={theme.color.primaryDark}
												stroke={theme.color.primaryContrast}
												strokeWidth="2"
											/>
										</g>
									</g>
								);
							})}
						</motion.g>
					)}
				</AnimatePresence>
			</svg>
		</div>
	);
};

const MarketProportionsBackground = ({ revealed }) => {
	return <Graph currentStep={values.length - 1} revealed={revealed} />;
};
export default MarketProportionsBackground;
